import { isStrayCypherExpr } from './stray'

/**
 * Detail bundle for a single `:Equipment` (`$equipmentId` param).
 *
 * This is the most complex per-id query on the Plant Viewer: it
 * stitches together the equipment node, its property substructure,
 * its directly-materialized UDT instances, and its location +
 * compute context in one round-trip.
 *
 * Returns:
 *   - `eq`                — the equipment node itself
 *   - `propBundles[]`     — every sensor target wired via the
 *                           spec-mandated indirected chain
 *                           `Equipment -[:HAS_OBSERVABLE|
 *                            HAS_SETPOINT|HAS_PROPERTY]-> {prop} -
 *                            [:MEASURED_BY|SET_BY]-> {Tag | PLCTag |
 *                            UDTInstance}`. The three sub-collections
 *                           are stamped with `kind: 'observable' |
 *                           'setpoint' | 'property'` so the client
 *                           can render them with the right pill.
 *   - `udtBundles[]`      — every `:UDTInstance` this equipment
 *                           directly `MATERIALIZES`, each paired
 *                           with up to 200 of its `:HAS_MEMBER`
 *                           members (Tag / UDTMember; ordered by
 *                           name)
 *   - `parentEquipment[]` — ancestor Equipment via inbound
 *                           `HAS_EQUIPMENT` (rootward first)
 *   - `line` / `site` / `process` — the line reached via
 *                           `(eq)<-[:HAS_EQUIPMENT*1..8]-(line:Line)`,
 *                           plus the owning `:Site`, reached by walking
 *                           nested `<-[:HAS_SUBLINE*0..]-` lines up to
 *                           the top line a Site owns via `HAS_LINE` (so
 *                           deeply-nested sublines still resolve a
 *                           site), and the line's `INSTANTIATES`-bound
 *                           `:Process`
 *   - `gateway`           — the readable live-data `:Compute`, found by
 *                           walking `equipment -CONTROLLED_BY-> Controller
 *                           <-MAPS_TO_CONTROLLER- Compute -NETWORKS_WITH-
 *                           gateway`. The mapped Compute (OPC/device) is
 *                           the tags' source, not an endpoint; the gateway
 *                           it `NETWORKS_WITH` (the Ignition gateway) is
 *                           what serves `[provider]` tag paths. Prefers a
 *                           non-OPC `url`/`gateway_url`, falling back to
 *                           the mapped compute, the site's `HAS_COMPUTE`
 *                           gateways, then any `MONITORED_BY` compute
 *   - `controller`        — the first `:Controller` the equipment
 *                           is `CONTROLLED_BY`
 *   - `controllerTags[]`  — `:Tag` nodes the controller's PLC tags
 *                           `MAPS_TO_TAG` (controller → SCADA tag
 *                           bridge)
 *   - `opcComputeTags[]`  — `:Tag` nodes from any external OPC-
 *                           server `:Compute` that
 *                           `MAPS_TO_CONTROLLER` this controller
 *                           (Compute `compute_type = 'opc'`)
 *   - `computes[]`        — every `:Compute` the equipment itself
 *                           is `MONITORED_BY`
 *   - `robotControllers[]` — `:RobotController` nodes that `CONTROLS`
 *                           this equipment (robotic-cell bridge)
 *   - `utilities[]` / `utilityEdges[]` — `:Utility` nodes required by
 *                           this equipment and their connection specs
 *
 * Bounded by `LIMIT 10000`.
 */
export const PLANT_EQUIPMENT_DETAIL_CYPHER = `
            MATCH (eq:Equipment {id: $equipmentId})

            OPTIONAL MATCH (eq)-[:HAS_OBSERVABLE]->(obs)
            OPTIONAL MATCH (obs)-[:MEASURED_BY]->(obsTag)
              WHERE (obsTag:Tag OR obsTag:PLCTag OR obsTag:UDTInstance)
            WITH eq, collect(DISTINCT
              CASE WHEN obs IS NULL THEN NULL
              ELSE { prop: obs, kind: 'observable', tag: obsTag } END
            ) AS observables

            OPTIONAL MATCH (eq)-[:HAS_SETPOINT]->(sp)
            OPTIONAL MATCH (sp)-[:SET_BY]->(spTag)
              WHERE (spTag:Tag OR spTag:PLCTag OR spTag:UDTInstance)
            WITH eq, observables, collect(DISTINCT
              CASE WHEN sp IS NULL THEN NULL
              ELSE { prop: sp, kind: 'setpoint', tag: spTag } END
            ) AS setpoints

            OPTIONAL MATCH (eq)-[:HAS_PROPERTY]->(ep)
            OPTIONAL MATCH (ep)-[:MEASURED_BY]->(epTag)
              WHERE (epTag:Tag OR epTag:PLCTag OR epTag:UDTInstance)
            WITH eq, observables, setpoints, collect(DISTINCT
              CASE WHEN ep IS NULL THEN NULL
              ELSE { prop: ep, kind: 'property', tag: epTag } END
            ) AS unclassified

            WITH eq,
                 [b IN observables WHERE b IS NOT NULL]
                 + [b IN setpoints WHERE b IS NOT NULL]
                 + [b IN unclassified WHERE b IS NOT NULL]
                 AS propBundles

            OPTIONAL MATCH (eq)-[:MATERIALIZES]->(udt:UDTInstance)
            OPTIONAL MATCH (udt)-[:HAS_MEMBER]->(udtMember)
              WHERE (udtMember:UDTMember OR (udtMember:Tag AND udtMember.ignition_tag_type = 'udt_member'))
            WITH eq, propBundles, udt, udtMember
            ORDER BY udt.name ASC, udtMember.name ASC
            WITH eq, propBundles, udt,
                 [m IN collect(udtMember) WHERE m IS NOT NULL][..200] AS udtMembers
            WITH eq, propBundles,
                 collect(DISTINCT
                   CASE WHEN udt IS NULL THEN NULL
                   ELSE { instance: udt, members: udtMembers } END
                 ) AS udtBundles

            CALL {
              WITH eq
              OPTIONAL MATCH path = (ancestor:Equipment)-[:HAS_EQUIPMENT*1..8]->(eq)
              WITH ancestor, min(length(path)) AS hops
              WHERE ancestor IS NOT NULL
              ORDER BY hops DESC
              RETURN collect(ancestor) AS parentEquipment
            }
            OPTIONAL MATCH (eq)<-[:HAS_EQUIPMENT*1..8]-(line:Line)
            OPTIONAL MATCH (line)<-[:HAS_SUBLINE*0..20]-(:Line)<-[:HAS_LINE]-(site:Site)
            WITH eq, propBundles, udtBundles, parentEquipment,
                 head(collect(DISTINCT line)) AS line,
                 head(collect(DISTINCT site)) AS site
            OPTIONAL MATCH (line)-[:INSTANTIATES]->(process:Process)
            OPTIONAL MATCH (eq)-[:CONTROLLED_BY]->(controller:Controller)

            OPTIONAL MATCH (controller)-[:HAS_TAG]->(plc:PLCTag)-[:MAPS_TO_TAG]->(ctrlTag:Tag)

            OPTIONAL MATCH (controller)<-[:MAPS_TO_CONTROLLER]-(opcCompute:Compute)<-[:DERIVES_FROM]-(opcTag:Tag)
              WHERE opcCompute.compute_type = 'opc'
            OPTIONAL MATCH (eq)-[:MONITORED_BY]->(mc:Compute)
            OPTIONAL MATCH (robotController:RobotController)-[:CONTROLS]->(eq)
            OPTIONAL MATCH (eq)-[ru:REQUIRES_UTILITY]->(util:Utility)

            OPTIONAL MATCH (controller)<-[:MAPS_TO_CONTROLLER]-(mappedCompute:Compute)
            OPTIONAL MATCH (mappedCompute)-[:NETWORKS_WITH]-(gwCompute:Compute)
            OPTIONAL MATCH (site)-[:HAS_COMPUTE]->(siteCompute:Compute)
            WITH eq, propBundles, udtBundles, parentEquipment, line, site, process,
                 head(collect(DISTINCT controller)) AS controller,
                 [t IN collect(DISTINCT ctrlTag) WHERE t IS NOT NULL] AS controllerTags,
                 [t IN collect(DISTINCT opcTag) WHERE t IS NOT NULL] AS opcComputeTags,
                 collect(DISTINCT mc) AS monitorComputes,
                 collect(DISTINCT robotController) AS robotControllers,
                 collect(DISTINCT util) AS utilities,
                 collect(DISTINCT {
                   type: type(ru),
                   source: eq.id,
                   target: util.id,
                   props: properties(ru)
                 }) AS utilityEdges,
                 collect(DISTINCT gwCompute) AS gatewayComputes,
                 collect(DISTINCT mappedCompute) AS mappedComputes,
                 collect(DISTINCT siteCompute) AS siteComputes
            WITH eq, propBundles, udtBundles, parentEquipment, line, site, process, controller,
                 controllerTags, opcComputeTags, monitorComputes, robotControllers,
                 utilities, utilityEdges,
                 [c IN gatewayComputes + mappedComputes + siteComputes + monitorComputes
                    WHERE coalesce(c.url, c.gateway_url) IS NOT NULL] AS urlComputes
            RETURN eq, propBundles,
                   [b IN udtBundles WHERE b IS NOT NULL] AS udtBundles,
                   parentEquipment, line, site, process,
                   coalesce(
                     head([c IN urlComputes
                           WHERE NOT toLower(coalesce(c.url, c.gateway_url)) STARTS WITH 'opc.']),
                     head(urlComputes)
                   ) AS gateway,
                   controller, controllerTags, opcComputeTags,
                   [r IN robotControllers WHERE r IS NOT NULL] AS robotControllers,
                   monitorComputes AS computes,
                   utilities, utilityEdges,
                   ${isStrayCypherExpr('eq', 'Equipment')} AS isStray
            LIMIT 10000
          `
